/**
 * La campana: los avisos de esta persona, desde cualquier pantalla.
 *
 * **Por qué existe.** Los avisos vivían en un panel dentro de Mi Trabajo, así que el contador de
 * no leídos **sólo existía estando parado ahí**: alguien que trabaja todo el día en Tickets no
 * se enteraba de nada hasta que entraba a otra pantalla por casualidad.
 *
 * **Y llegaban mezclados dos mostradores.** A la misma bandeja caían los avisos de lo que le toca
 * hacer a uno —te asignaron una tarea, entró un caso a la cola de tu rol— y los de lo que uno
 * pidió —tu pedido cambió de estado—. Lo segundo no es trabajo de nadie, y estaba ocupando la
 * pantalla del trabajo. El backend ahora guarda **a qué título** le llegó cada aviso
 * (`services/avisos.TITULOS`) y acá se separan en dos pestañas.
 *
 * Lo que la campana **no** hace: no es una lista de tareas. Una tarea se hace, un aviso se lee.
 * Por eso el panel de Mi Trabajo no desaparece — se queda con lo que es trabajo y se acorta.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, Check, Loader2 } from 'lucide-react';
import { myWorkAPI } from '@/lib/api';
import { fechaCorta } from '@/lib/fechas';

const TRABAJO = 'trabajo';
const PEDIDO = 'pedido';

//: Cuántos se traen por página. Chico a propósito: la campana se abre para mirar lo último, no
//: para auditar el historial — para eso está "ver más", que no tiene tope.
const POR_PAGINA = 15;

export default function CampanaDeAvisos({ tenantId, compacta = false }) {
  const [abierta, setAbierta] = useState(false);
  const [solapa, setSolapa] = useState(TRABAJO);
  // **Cuál está desplegado.** El texto se corta a dos líneas para que la lista siga siendo una
  // lista, pero un aviso largo quedaba ilegible y no había forma de verlo entero: la campana es
  // el único lugar donde vive, así que truncar sin salida es perder el contenido.
  const [desplegado, setDesplegado] = useState(null);
  const [datos, setDatos] = useState({ avisos: [], sin_leer: 0, sin_leer_por_titulo: {}, hay_mas: false });
  const [cargando, setCargando] = useState(false);
  const caja = useRef(null);

  const cargar = useCallback(async (titulo, offset = 0) => {
    if (!tenantId) return;
    setCargando(true);
    try {
      const res = await myWorkAPI.listAvisos(tenantId, { titulo, limit: POR_PAGINA, offset });
      setDatos((prev) => ({
        ...res.data,
        avisos: offset ? [...prev.avisos, ...(res.data.avisos || [])] : (res.data.avisos || []),
      }));
    } catch {
      // **Un fallo acá no puede tapar la pantalla.** La campana es accesoria: si no se puede
      // leer la bandeja, lo que la persona estaba haciendo sigue.
      setDatos((prev) => ({ ...prev, avisos: [] }));
    } finally {
      setCargando(false);
    }
  }, [tenantId]);

  // El contador se pide al montar y cada dos minutos, esté abierta o no: es la única forma de
  // que sirva desde otra pantalla. No es un websocket a propósito — un número que se atrasa dos
  // minutos es suficiente, y una conexión viva por esto no se justifica.
  useEffect(() => {
    if (!tenantId) return undefined;
    cargar(solapa);
    const t = setInterval(() => cargar(solapa), 120000);
    return () => clearInterval(t);
  }, [tenantId, solapa, cargar]);

  useEffect(() => {
    if (!abierta) return undefined;
    const fuera = (e) => { if (caja.current && !caja.current.contains(e.target)) setAbierta(false); };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierta]);

  const porTitulo = datos.sin_leer_por_titulo || {};
  const totalSinLeer = (porTitulo[TRABAJO] || 0) + (porTitulo[PEDIDO] || 0);

  const marcarUno = async (a) => {
    if (a.leido_at) return;
    setDatos((p) => ({
      ...p,
      avisos: p.avisos.map((x) => (x.aviso_id === a.aviso_id ? { ...x, leido_at: 'ya' } : x)),
      sin_leer: Math.max(0, (p.sin_leer || 1) - 1),
      sin_leer_por_titulo: { ...porTitulo, [solapa]: Math.max(0, (porTitulo[solapa] || 1) - 1) },
    }));
    try { await myWorkAPI.marcarAvisoLeido(tenantId, a.aviso_id); } catch { cargar(solapa); }
  };

  const marcarTodos = async () => {
    try { await myWorkAPI.marcarTodosLeidos(tenantId, solapa); } finally { cargar(solapa); }
  };

  return (
    <div className="relative" ref={caja}>
      {/* **Con rótulo y no sólo el ícono.** Quedó como una campanita gris de 16 px en el fondo
          oscuro del sidebar, entre la flecha de colapsar y el botón de salir — y sin avisos sin
          leer no tiene badge, así que es un ícono apagado entre otros dos. Nadie la encontró.
          Un acceso que hay que buscar no existe. */}
      <button
        type="button"
        data-testid="campana-avisos"
        onClick={() => setAbierta((v) => !v)}
        className={`relative flex items-center gap-2 rounded-sm text-zinc-300 hover:text-white hover:bg-zinc-800 ${
          compacta ? 'p-2 justify-center' : 'w-full px-2 py-1.5 text-sm'
        }`}
        title={totalSinLeer ? `${totalSinLeer} sin leer` : 'Avisos'}
      >
        <Bell className="w-4 h-4 shrink-0" />
        {!compacta && <span>Avisos</span>}
        {totalSinLeer > 0 && (
          <span
            data-testid="campana-contador"
            className={`min-w-[18px] h-[18px] px-1 rounded-full bg-brand-danger text-white text-[10px] font-bold flex items-center justify-center ${
              compacta ? 'absolute -top-0.5 -right-0.5' : 'ml-auto'
            }`}
          >
            {totalSinLeer > 99 ? '99+' : totalSinLeer}
          </span>
        )}
      </button>

      {abierta && (
        <div className="absolute left-0 bottom-full mb-2 w-[24rem] max-h-[32rem] overflow-hidden flex flex-col bg-white text-zinc-900 rounded-sm shadow-xl border border-zinc-200 z-50">
          <div className="flex items-center border-b border-zinc-200">
            {[[TRABAJO, 'Mi trabajo'], [PEDIDO, 'Mis pedidos']].map(([id, etiqueta]) => (
              <button
                key={id}
                type="button"
                data-testid={`campana-solapa-${id}`}
                onClick={() => setSolapa(id)}
                className={`flex-1 px-3 py-2 text-xs font-medium ${
                  solapa === id ? 'text-brand-primary border-b-2 border-brand-primary' : 'text-zinc-500'
                }`}
              >
                {etiqueta}
                {porTitulo[id] > 0 && (
                  <span className="ml-1 text-[10px] font-bold text-brand-danger">{porTitulo[id]}</span>
                )}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto">
            {(datos.avisos || []).length === 0 && !cargando && (
              <p className="text-xs text-zinc-400 text-center py-8">
                {solapa === TRABAJO ? 'Nada pendiente de tu trabajo.' : 'Sin novedades de tus pedidos.'}
              </p>
            )}
            {(datos.avisos || []).map((a) => (
              <button
                key={a.aviso_id}
                type="button"
                onClick={() => { marcarUno(a); setDesplegado(desplegado === a.aviso_id ? null : a.aviso_id); }}
                className={`w-full text-left px-3 py-2 border-b border-zinc-100 hover:bg-zinc-50 ${
                  a.leido_at ? 'bg-white' : 'bg-indigo-50/60'
                }`}
              >
                <div className="flex items-start gap-2">
                  {!a.leido_at && <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-1.5 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <p className={`text-xs truncate ${a.leido_at ? 'text-zinc-600' : 'font-semibold text-zinc-900'}`}>
                      {a.asunto}
                    </p>
                    <p
                      className={`text-[11px] text-zinc-500 whitespace-pre-wrap ${
                        desplegado === a.aviso_id ? '' : 'line-clamp-2'
                      }`}
                    >
                      {a.texto}
                    </p>
                    <p className="text-[10px] text-zinc-400 mt-0.5">
                      {fechaCorta(a.created_at)}
                      {(a.texto || '').length > 90 && (
                        <span className="ml-1 text-brand-primary">
                          {desplegado === a.aviso_id ? '· ver menos' : '· ver todo'}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </button>
            ))}
            {datos.hay_mas && (
              <button
                type="button"
                onClick={() => cargar(solapa, (datos.avisos || []).length)}
                className="w-full py-2 text-xs text-brand-primary hover:underline"
              >
                Ver más
              </button>
            )}
            {cargando && (
              <p className="text-center py-3"><Loader2 className="w-4 h-4 animate-spin inline text-zinc-400" /></p>
            )}
          </div>

          {porTitulo[solapa] > 0 && (
            <button
              type="button"
              data-testid="campana-marcar-todos"
              onClick={marcarTodos}
              className="border-t border-zinc-200 py-2 text-xs text-zinc-600 hover:text-brand-primary flex items-center justify-center gap-1"
            >
              <Check className="w-3 h-3" /> Marcar todo como leído
            </button>
          )}
        </div>
      )}
    </div>
  );
}
